// UltraFlex Platform - Auth Context with Role-Based Access
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'member', 'staff', or 'pt'
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('uf_token'));

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Backend returns user data directly, with user_type property for staff
        setUser(data);
        // Determine user type from response
        const detectedType = data.user_type || (data.pt_number ? 'pt' : data.member_number ? 'member' : 'staff');
        setUserType(detectedType);
      } else {
        // Token invalid
        logout();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const loginUnified = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/unified-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }
    
    localStorage.setItem('uf_token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('uf_refresh_token', data.refresh_token);
    }
    setToken(data.access_token);
    
    const uType = data.user_type;
    const userData = {
      id: data.user_id,
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
    };
    
    if (uType === 'staff') {
      userData.role = data.role;
      userData.site_ids = data.site_ids || [];
      userData.permissions = data.permissions || [];
      userData.employee_number = data.employee_number;
      userData.must_reset_password = data.must_reset_password;
    } else if (uType === 'member') {
      userData.member_number = data.member_number;
      userData.is_suspended = data.is_suspended;
      userData.member_status = data.member_status;
    } else if (uType === 'pt') {
      userData.pt_number = data.pt_number;
      userData.status = data.status;
    }
    
    setUser(userData);
    setUserType(uType);
    return data;
  };

  const loginMember = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }
    
    localStorage.setItem('uf_token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('uf_refresh_token', data.refresh_token);
    }
    setToken(data.access_token);
    // User data is included directly in login response
    setUser({ 
      id: data.user_id, 
      email: data.email, 
      first_name: data.first_name, 
      last_name: data.last_name,
      member_number: data.member_number,
      is_suspended: data.is_suspended,
      member_status: data.member_status,
    });
    setUserType('member');
    
    return data;
  };

  const loginStaff = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/staff-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }
    
    localStorage.setItem('uf_token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('uf_refresh_token', data.refresh_token);
    }
    setToken(data.access_token);
    // User data is included directly in login response
    setUser({ 
      id: data.user_id, 
      email: data.email, 
      first_name: data.first_name, 
      last_name: data.last_name,
      role: data.role,
      site_ids: data.site_ids || [],
      permissions: data.permissions || []
    });
    setUserType('staff');
    
    return data;
  };

  const loginPT = async (email, password) => {
    const response = await fetch(`${API_URL}/api/pt/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }
    
    localStorage.setItem('uf_token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('uf_refresh_token', data.refresh_token);
    }
    setToken(data.access_token);
    setUser({ 
      id: data.user_id, 
      email: data.email, 
      first_name: data.first_name, 
      last_name: data.last_name,
      pt_number: data.pt_number,
      status: data.status 
    });
    setUserType('pt');
    
    return data;
  };

  const registerMember = async (memberData) => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(memberData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Registration failed');
    }
    
    localStorage.setItem('uf_token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    setUserType('member');
    
    return data;
  };

  const logout = () => {
    localStorage.removeItem('uf_token');
    localStorage.removeItem('uf_refresh_token');
    setToken(null);
    setUser(null);
    setUserType(null);
  };

  const isStaff = useCallback(() => userType === 'staff', [userType]);
  const isMember = useCallback(() => userType === 'member', [userType]);
  const isPT = useCallback(() => userType === 'pt', [userType]);
  const hasRole = useCallback((role) => user?.role === role, [user]);
  
  const hasPermission = (permission) => {
    if (!user || userType !== 'staff') return false;
    
    const role = user.role;
    
    // Super admin has all permissions
    if (role === 'super_admin' || role === 'developer_admin') return true;
    
    // Define permission sets
    const permissions = {
      site_staff: ['view_members', 'search_members', 'view_access_logs', 'grant_temporary_access'],
      site_manager: ['view_members', 'search_members', 'update_member', 'view_access_logs', 'grant_temporary_access', 'view_site_dashboard', 'view_site_reports', 'manage_site_staff'],
      area_manager: ['view_members', 'search_members', 'update_member', 'view_access_logs', 'grant_temporary_access', 'view_site_dashboard', 'view_site_reports', 'view_estate_dashboard', 'view_estate_reports', 'manage_plans'],
      finance_admin: ['view_members', 'search_members', 'view_all_payments', 'view_collections', 'process_refund', 'view_settlement_batches', 'export_finance_reports']
    };
    
    return permissions[role]?.includes(permission) || false;
  };

  const value = {
    user,
    userType,
    token,
    loading,
    isAuthenticated: !!user,
    isStaff,
    isMember,
    isPT,
    hasRole,
    hasPermission,
    loginUnified,
    loginMember,
    loginStaff,
    loginPT,
    registerMember,
    logout,
    fetchCurrentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
